import { html, type TemplateResult } from 'lit-html';
import { get } from '../api.js';
import { app, emptyRow, helpLink, type Page } from '../app.js';
import { barChart, compareBars, plural } from '../charts.js';
import { duration, pct, statusPill, when } from '../format.js';
import { delta, DURATION_BUCKETS, num, period, periodName, periodSelect, type AgentMetrics, type HourMetrics, type QueueMetrics, type TeamMetrics } from '../metrics.js';
import { hourChart } from './reports.js';
import { showAgentTimeouts } from './timeouts.js';

interface AgentDay { day: string; wrapups: number; timeouts: number; avg_acw_seconds: number | null }
interface AgentTimeout { id: string; genesys_event_time: string; queue_id: string; queue_name: string | null; status: string; inactive_duration_seconds: number | null }
interface AgentReport {
  agentId: string;
  agentName: string | null;
  agent: AgentMetrics | null;
  team: TeamMetrics;
  daily: AgentDay[];
  queues: QueueMetrics[];
  hours: HourMetrics[];
  durations: { agent: Record<string, number>; team: Record<string, number> };
  recent: AgentTimeout[];
}

let data: AgentReport | null = null;

function kpi(label: string, value: string, team: TemplateResult | string, extra: TemplateResult | string = '') {
  return html`<div class="kpi"><span class="label">${label}</span><span class="value">${value}</span><span class="sub">${extra}${team}</span></div>`;
}

function kpis(a: AgentMetrics, t: TeamMetrics) {
  // The team's per-agent average, so a count compares with a typical agent rather than the total.
  const perAgent = (n: number) => {
    if (!t.agents) return '—';
    const v = n / t.agents;
    return v < 10 ? v.toFixed(1) : Math.round(v).toLocaleString();
  };
  return html`<div class="grid kpis four">
    ${kpi('Timeouts', num(a.timeouts), `Team average ${perAgent(t.timeouts)} per agent`, t.prev_covered ? html`${delta(a.timeouts, a.timeouts_prev, true)} · ` : '')}
    ${kpi('Timeout rate', pct(a.timeout_rate), `Team ${pct(t.timeout_rate)}`)}
    ${kpi('Wrap-ups', num(a.wrapups), `Team average ${perAgent(t.wrapups)} per agent`)}
    ${kpi('Avg ACW', duration(a.avg_acw_seconds), `Team ${duration(t.avg_acw_seconds)}`)}
    ${kpi('Avg own wrap-up', duration(a.avg_own_wrapup_seconds), `Team ${duration(t.avg_own_wrapup_seconds)}`)}
    ${kpi('Close calls', num(a.close_calls), `Team ${pct(t.close_call_rate)} of own wrap-ups`, html`${pct(a.close_call_rate)} of own wrap-ups · `)}
    ${kpi('ACW in timeouts', a.timeout_acw_seconds ? duration(a.timeout_acw_seconds) : '—', `Total ACW ${duration(a.total_acw_seconds)}`)}
    ${a.deactivations ? kpi('Deactivations', num(a.deactivations), `Avg inactive ${duration(a.avg_inactive_seconds)}`) : ''}
  </div>`;
}

function shares(counts: Record<string, number>) {
  const total = Object.values(counts).reduce((n, v) => n + v, 0);
  return (k: string) => (total ? (counts[k] ?? 0) / total : 0);
}

function dailyChart(rows: AgentDay[]) {
  return barChart(
    'agent-daily',
    rows.map((d) => ({
      label: d.day.slice(5),
      values: [d.timeouts],
      tip: html`<strong>${d.day}</strong><span><i class="sw s1"></i>${plural(d.timeouts, 'timeout')}</span>
        <span>${plural(d.wrapups, 'wrap-up')}${d.avg_acw_seconds !== null ? `, avg ACW ${duration(d.avg_acw_seconds)}` : ''}</span>`,
    })),
    `Timeouts per day, last ${rows.length} days`,
  );
}

export const agentPage: Page = {
  title: 'Agent',
  async load() {
    data = await get<AgentReport>(`/reports/agents/${encodeURIComponent(app.routeParam)}?days=${period.days}`);
  },
  view() {
    const d = data;
    if (!d || d.agentId !== app.routeParam) return html`<p><a href="#/reports">← Reports</a></p>`;
    const a = d.agent;
    const mine = shares(d.durations.agent);
    const everyone = shares(d.durations.team);
    return html`
      <p class="crumb"><a href="#/reports">← Reports</a></p>
      <div class="row-between page-head">
        <h1>${d.agentName ?? 'Unknown agent'} ${helpLink('reports')}</h1>
        ${periodSelect(agentPage.load)}
      </div>
      <p class="hint">Genesys user <code class="id">${d.agentId}</code>. Last ${periodName()}${period.days < 7 ? ' (the daily chart shows 7 days)' : ''}, compared with the whole team.</p>
      ${a ? kpis(a, d.team) : html`<div class="banner info">No wrap-ups or timeouts for this agent in the last ${periodName()}.</div>`}

      <section class="panel">
        <h2>How long their wrap-ups take</h2>
        <p class="hint">Share of wrap-ups by time in ACW, this agent against the team.</p>
        <div class="legend"><span><i class="sw s1"></i>${d.agentName ?? 'This agent'}</span><span><i class="sw s2"></i>Team</span></div>
        ${compareBars(DURATION_BUCKETS.map(([k, label]) => ({ label, a: mine(k), b: everyone(k) })), [d.agentName ?? 'This agent', 'Team'], 'Wrap-up durations, agent against team')}
      </section>

      <section class="panel">
        <h2>Timeouts per day</h2>
        ${dailyChart(d.daily)}
      </section>

      <section class="panel">
        <h2>Timeouts by hour of day</h2>
        ${hourChart('agent-hours', d.hours)}
      </section>

      <section>
        <h2>By queue</h2>
        <div class="table-wrap"><table class="metrics">
          <thead><tr><th>Queue</th><th class="num">ACW limit</th><th class="num">Timeouts</th><th class="num">Rate</th><th class="num">Wrap-ups</th>
            <th class="num">Avg ACW</th><th class="num">Avg own wrap-up</th><th class="num">Close calls</th></tr></thead>
          <tbody>${d.queues.length
            ? d.queues.map((q) => html`<tr><td>${q.queue_name ?? q.queue_id}</td><td class="num">${duration(q.acw_limit_seconds)}</td><td class="num">${num(q.timeouts)}</td>
                <td class="num">${pct(q.timeout_rate)}</td><td class="num">${num(q.wrapups)}</td><td class="num">${duration(q.avg_acw_seconds)}</td>
                <td class="num">${duration(q.avg_own_wrapup_seconds)}</td><td class="num">${num(q.close_calls)}</td></tr>`)
            : emptyRow(8, 'No wrap-ups in this period.')}</tbody>
        </table></div>
      </section>

      <section>
        <h2>Latest timeouts</h2>
        <div class="table-wrap"><table>
          <thead><tr><th>When</th><th>Queue</th><th>Outcome</th><th class="num">Inactive for</th></tr></thead>
          <tbody>${d.recent.length
            ? d.recent.map((r) => html`<tr><td class="nowrap">${when(r.genesys_event_time)}</td><td>${r.queue_name ?? r.queue_id}</td><td>${statusPill(r.status)}</td>
                <td class="num">${duration(r.inactive_duration_seconds)}</td></tr>`)
            : emptyRow(4, 'No timeouts recorded for this agent.')}</tbody>
        </table></div>
        ${d.recent.length ? html`<p><a href="#/timeouts" @click=${(e: Event) => { e.preventDefault(); showAgentTimeouts(d.agentId); }}>All of this agent's timeouts →</a></p>` : ''}
      </section>`;
  },
};
